import React, { useEffect, useRef, useState } from "react";
import { setJourneyToken } from "@/lib/journeyToken";
import {
  useParams,
  useSearchParams,
  useNavigate,
  useLocation,
} from "react-router-dom";
import config from "@/config";
import { useRecoilState, useRecoilValue } from "recoil";
import { lenderConfigAtom } from "@/recoil/atom/lenderConfigAtom";
import { employmentRulesFor, isWorkDetailsDone } from "@/b2b/lib/employmentRules";
import { userDataAtom } from "@/recoil/atom/userDataAtom";
import { useTheme } from "@/b2b/context/ThemeContext";
import BankDetails from "@/components/banking/bank_details";
import B2BDynamicScreen from "@/b2b/pages/B2BDynamicScreen.jsx";
import OnboardingSteps from "@/b2b/pages/OnboardingSteps";
import WorkDetails from "@/b2b/pages/WorkDetails";
import ESign from "@/components/banking/esign";
import PennyDrop from "@/b2b/pages/PennyDrop";
import Mandate from "@/components/banking/mandate";
import RentSummary from "./RentSummary";
import SorryScreen from "./Sorry";
import CongratsScreen from "./Congrats";
import { fetchData, getDataB2B, handleSendSlack } from "@/lib/helper";
import ProcessingScreen from "./processing";
import RedirectScreenHelper from "./RedirectScreenHelper";
import HappyMoveIn from "./HappyMoveInScreen";
import { navigationRules } from "@/b2b/config/partnerJourney";
import { fetchJourneyWithGates } from "@/b2b/lib/journeyGates";
import { bankingEntryRedirect, notEligibleStep } from "@/b2b/lib/bankingEntryCheck";
import NotFoundPage from "./NotFoundPage";
import BankingLimitExceededScreen from "./BankingLimitExceededScreen";
import BankingSorry from "./BankingSorryScreen";
import PennyDropSorryScreen from "./PennyDropSorryScreen";
import BankingReviewScreen from "./BankingReviewScreen";
import RedirectScreen from "./RedirectScreen";
import TransitionScreen from "./TransitionScreen";
import SignUpScreen from "./SignUpScreen";
import PropertyDetailsScreen from "./PropertyDetailsScreen";
import GuarantorScreen from "./GuarantorScreen";
import { faL } from "@fortawesome/free-solid-svg-icons";
import MaintenanceScreen from "./MaintenanceScreen";
import TenureScreen from "./TenureScreen";

export default function HomeScreen() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { userIdentifier } = useParams();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isFetched, setIsFetched] = useState(false);

  /** ✅ Get `step` & `partner` Directly from the URL */

  const partner = searchParams.get("partner") || "";
  const [step, setStep] = useState("loading");

  // Read inside the async step resolution below, so it must see the config as it is THEN, not as
  // it was when that closure was created. Before the live config loads it holds only the baked-in
  // profiles, which carry no employment rules → today's gate.
  const lenderConfig = useRecoilValue(lenderConfigAtom);
  const lenderConfigRef = useRef(lenderConfig);
  lenderConfigRef.current = lenderConfig;
  // The journey's lender as the step resolution below last read it from `journey/full` — handed
  // to Work Details so it never judges the work-email rule by a stale session cache (review I1).
  const [journeyLenderKey, setJourneyLenderKey] = useState(null);

  // A link OPENED on `step=banking` skips pre-flight (see the fetch effect below), so the
  // banking screen is held until the server confirms the tenant is not a not-eligible dead end
  // (bankingEntryCheck.js). "pending" → checking; "done" → render banking; "redirected" →
  // leaving for another step (flips to "done" once the step changes, so a later in-app visit
  // to banking renders normally). Every other entry starts at "done": nothing changes for it.
  const [bankingEntry, setBankingEntry] = useState(() => {
    const openedOn = (searchParams.get("step") || "").replace(/[^a-zA-Z0-9-_]/g, "");
    return openedOn === "banking" && partner && !["xu"].includes(userIdentifier)
      ? "pending"
      : "done";
  });

  useEffect(() => {
    const urlStep = searchParams.get("step") || "loading";
    const cleanStep = urlStep.replace(/[^a-zA-Z0-9-_]/g, "");
    setStep(cleanStep);
  }, [searchParams]);

  const fetchUserData = async () => {
    try {
      const currentLocation = window.location.pathname + window.location.search;
      let result;
      result = await getDataB2B("b2b/onboard/pre-flight", userIdentifier);
      setUserData(result?.data);
      // if (result?.data?.pan_eligible === null) {
      //   await fetch(`${config.API_URL}/b2b/onboard/check-eligibility`, {
      //     method: "POST",
      //     headers: {
      //       "Content-Type": "application/json",
      //       Authorization: `Short ${localStorage.getItem("user_identifier")}`,
      //     },
      //   });

      //   result = await getDataB2B("b2b/onboard/pre-flight", userIdentifier);
      // }

      const newPartner = result.tag;
      localStorage.setItem("partnerIdentifier", newPartner);
      console.log("New Partner:", newPartner, result);

      if (!result.status) {
        navigate(`/b/${userIdentifier}?partner=${newPartner}&step=sign-up`, {
          state: {
            phone: result?.data?.phone,
            name: result?.data?.full_name,
            email: result?.data?.email,
          },
        });
      } else {
        if (result.onboarding_token)
          setJourneyToken(result.onboarding_token);

        // Check if the tenant's real, active Journey already has an engaged BANKING step
        // when step is missing from URL. This must come from the actual Journey (fetched
        // via journeyGates.js, the same source OnboardingSteps/Congrats/bank_details use)
        // — `result.data` here is a Lead, which has no `steps` field, so it can never
        // carry real step state.
        if (!searchParams.get("step")) {
          const { journey: activeJourney, gates } = await fetchJourneyWithGates();
          const bankingStep = activeJourney?.steps?.find(
            (step) => step?.name === "BANKING"
          );
          const bankingStatus = bankingStep?.status?.toUpperCase();

          // Two cases are deliberately left to the legacy heuristic below instead of
          // being intercepted here:
          //  - no real BANKING step yet, or one still PENDING *and* LOCKED — genuinely
          //    not reached, so the pre-banking flow (property/work details, etc.) is
          //    correct.
          //  - BANKING already SUCCEEDED — the legacy branch below has extra nuance for
          //    this case (rentSummaryConsent decides rent-summary vs onboarding) that
          //    isn't reproduced here, so SUCCESS is left to it on purpose.
          // Every other real status — RETRY/WAITING/IN_REVIEW/FAILED/REJECTED, or
          // PENDING while UNLOCKED — means the tenant has already engaged Banking and
          // must go straight back to it. This is checked by `status`, not just `state`,
          // because a step can be re-LOCKED while its result is still pending (verified
          // against production: 130 active journeys have BANKING LOCKED+WAITING/
          // IN_REVIEW, and some of those already carry a stale/null `Lead.bank_eligible`
          // — the exact mirror the legacy heuristic below would otherwise misread as
          // "banking never attempted").
          const bankingNotYetReached =
            !bankingStep ||
            (bankingStatus === "PENDING" && bankingStep.state !== "UNLOCKED");

          if (bankingStep && bankingStatus !== "SUCCESS" && !bankingNotYetReached) {
            // Server-computed gate (cpcore journey-gates.ts, `AppConfigKey.WORK_DETAILS_BANKING_GATE_MODE`):
            // may this tenant see banking/banking-review at all, or did they land here — e.g. via
            // an async fexpex webhook flipping BANKING mid-session — without ever completing Work
            // Details? Checked BEFORE picking a destination, because a `false` here must send the
            // tenant to work-details, never to either banking screen. That is why this does NOT go
            // through `evaluateGate()`: its shared `fallbackStep` resolves to banking/banking-review
            // for every OTHER gate (their failure means "go finish banking"), which is the opposite
            // of what this gate's failure means ("go do work-details first").
            const workDetailsRequired =
              gates?.enforcement?.banking === "apply" && gates?.allowed?.banking === false;
            if (workDetailsRequired) {
              navigate(`/b/${userIdentifier}?partner=${newPartner}&step=work-details`);
              return;
            }

            if (bankingStatus === "IN_REVIEW" || bankingStatus === "WAITING") {
              navigate(`/b/${userIdentifier}?partner=${newPartner}&step=banking-review`);
            } else {
              navigate(`/b/${userIdentifier}?partner=${newPartner}&step=banking`);
            }
            return;
          }
        }

        let newStep;

        const isEligible = result?.data?.is_eligible;
        const panEligible = result?.data?.pan_eligible ?? null;
        const bankEligible = result?.data?.bank_eligible;
        const isStudent = result?.data?.isStudent ?? null;
        const guarantorPhone = result?.data?.guarantorPhone ?? null;

        console.log(result?.data);

        if (isEligible === true && bankEligible === true) {
          if (!result?.data?.rentSummaryConsent) {
            newStep =
              navigationRules[newPartner]?.rentSummary ||
              navigationRules.default.rentSummary;
          } else {
            newStep =
              navigationRules[newPartner]?.isHardEligible ||
              navigationRules.default.isHardEligible;
          }
        } else if (isEligible === true && bankEligible !== true) {
          newStep = "banking-review";
        } else if (isEligible === false) {
          // penny-drop-sorry / guarantor-details / the partner's not-eligible screen — shared
          // with the step=banking entry check so both reloads agree (bankingEntryCheck.js).
          newStep = notEligibleStep(result?.data, newPartner, navigationRules);
        } else {
          if (isStudent && panEligible === null) {
            newStep = "guarantor-details";
          } else if (panEligible && bankEligible === null) {
            console.log("ooh shit no", result?.data);

            if (!result?.data?.property_address) {
              newStep =
                navigationRules[newPartner]?.propertyDetails ||
                navigationRules.default.propertyDetails;
            }

            // TODO: collect employment data here
            else {
              console.log(guarantorPhone, "guarantorPhone");
              const start = await getDataB2B(
                "b2b/onboard/start",
                userIdentifier,
              );
              // Without this token `journey/full` reads no journey (or another tenant's), so the
              // lender is unknown and Work Details asks for a work email even for Satya.
              setJourneyToken(start?.token);

              const journey = await fetchData("journey/full");

              // Self-employed users never provide a work email, so don't gate
              // their completion on workEmail — treat them as done once the
              // employment record exists. Same for any tenant whose journey lender's
              // profile asks for no work email (employmentRules.js).
              const employment = journey?.journey?.employment;
              setJourneyLenderKey(journey?.journey?.lender ?? null);
              const workDetailsDone = isWorkDetailsDone(
                employment,
                employmentRulesFor(lenderConfigRef.current?.lenders, journey?.journey?.lender),
              );

              if (!workDetailsDone) {
                console.log("employment data not found");

                if (isStudent && !guarantorPhone) {
                  newStep =
                    navigationRules[newPartner]?.guarantorDetails ||
                    navigationRules.default.guarantorDetails;
                } else {
                  newStep =
                    navigationRules[newPartner]?.workDetails ||
                    navigationRules.default.workDetails;
                }

                console.log("employment data not found", newStep);
              } else {
                newStep =
                  navigationRules[newPartner]?.bankingNotChecked ||
                  navigationRules.default.bankingNotChecked;
              }
            }
          } else if (!panEligible) {
            newStep =
              navigationRules[newPartner]?.panNotChecked ||
              navigationRules.default.panNotChecked;
          } else {
            newStep =
              navigationRules[newPartner]?.notHardEligible ||
              navigationRules.default.notHardEligible;
          }
        }

        console.log("newStep", newStep, "171");

        // Safety net: never emit `step=undefined` (it lands on the 404 page). If
        // no rule matched, continue onboarding from a safe default instead.
        const safeStep = newStep || navigationRules.default.propertyDetails;
        navigate(
          `/b/${userIdentifier}?partner=${newPartner}&step=${safeStep}`,
        );
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  /** Opened on `step=banking`: ask pre-flight once before the banking screen mounts. */
  useEffect(() => {
    if (bankingEntry !== "pending") return undefined;
    let cancelled = false;
    // A hung check must not hold the tenant on the loader: after 8 s show banking (fail open).
    const timer = setTimeout(() => {
      if (cancelled) return;
      cancelled = true;
      setBankingEntry("done");
    }, 8000);
    (async () => {
      let redirect = null;
      try {
        const preflight = await getDataB2B("b2b/onboard/pre-flight", userIdentifier);
        // Only a not-eligible answer needs the journey; everyone else pays one call.
        if (preflight?.status && preflight?.data?.is_eligible === false) {
          if (preflight.onboarding_token) setJourneyToken(preflight.onboarding_token);
          const { journey } = await fetchJourneyWithGates();
          redirect = bankingEntryRedirect({
            preflight,
            journey,
            partner,
            rules: navigationRules,
          });
        }
      } catch (error) {
        // Fail open: no answer must never strand a tenant who may do banking.
        console.error("Banking entry check failed; showing banking", error);
      }
      if (cancelled) return;
      clearTimeout(timer);
      if (redirect) {
        setBankingEntry("redirected");
        navigate(
          `/b/${userIdentifier}?partner=${redirect.partner}&step=${redirect.step}`,
        );
      } else {
        setBankingEntry("done");
      }
    })();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // Runs for the opening URL only; later steps never re-enter "pending".
  }, [bankingEntry]);

  useEffect(() => {
    if (bankingEntry === "redirected" && step !== "banking" && step !== "loading") {
      setBankingEntry("done");
    }
  }, [bankingEntry, step]);

  /** ✅ Store `userIdentifier` in localStorage */
  useEffect(() => {
    if (userIdentifier) {
      localStorage.setItem("user_identifier", userIdentifier);
    }
  }, [userIdentifier]);

  /** ✅ Always sync partner from URL so localStorage never holds a stale value */
  useEffect(() => {
    if (partner) {
      localStorage.setItem("partnerIdentifier", partner);
    }
  }, [partner]);

  /** ✅ Fetch User Data Only If `step` is Missing */
  useEffect(() => {
    // Add the pages error pages URLs below to redirect users from error page on refresh
    if (!["xu"].includes(userIdentifier)) {
      if (!step || !partner || ["sorry"].includes(step)) {
        fetchUserData();
      }
    }
  }, [userIdentifier, step, partner]);

  //commented

  // useEffect(() => {
  //   if (searchParams.get("step") !== step) {
  //     setSearchParams({ partner, step });
  //   }
  // }, [step, partner, setSearchParams]);

  if (!theme) return null;

  /** ✅ Render Component Based on `step` */
  switch (step) {
    case "property-details":
      return <PropertyDetailsScreen theme={theme} />;
    case "congrats":
      return <CongratsScreen theme={theme} />;
    case "sorry":
      return <SorryScreen theme={theme} />;
    case "rent-summary":
      return <RentSummary theme={theme} />;
    case "eligibility-check":
      return <B2BDynamicScreen step={step} />;
    case "guarantor-details":
      return <GuarantorScreen theme={theme} />;
    case "tenure-details":
      return <TenureScreen theme={theme} />;
    case "maintenance-details":
      return <MaintenanceScreen theme={theme} />;
    case "alt-phone":
      return <B2BDynamicScreen step={step} />;
    case "onboarding":
      return <OnboardingSteps theme={theme} />;
    case "work-details":
      return <WorkDetails leadData={userData} journeyLenderKey={journeyLenderKey} />;
    case "banking":
      return bankingEntry === "done" ? (
        <BankDetails theme={theme} />
      ) : (
        <ProcessingScreen theme={theme} recoverable />
      );
    case "penny-drop":
      return <PennyDrop theme={theme} />;
    case "esign":
      return <ESign theme={theme} />;
    case "mandate":
      return <Mandate theme={theme} />;
    case "happy-move-in":
      return <HappyMoveIn />;
    case "loading":
      return <ProcessingScreen theme={theme} recoverable />; // Fallback UI
    case "redirect":
      return <RedirectScreenHelper theme={theme} />;
    case "processing":
      return <ProcessingScreen theme={theme} recoverable />;
    case "banking-sorry":
      return <BankingSorry theme={theme} />;
    case "penny-drop-sorry":
      return <PennyDropSorryScreen theme={theme} />;
    case "banking-review":
      return <BankingReviewScreen theme={theme} partner={partner} />;
    case "banking-limit":
      return <BankingLimitExceededScreen theme={theme} />;
    case "back-to-base":
      return <RedirectScreen theme={theme} />;
    case "trn":
      return <TransitionScreen />;
    case "sign-up":
      return <SignUpScreen theme={theme} />;
    default:
      return <NotFoundPage />;
  }
}
